import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { AddressBook, FloppyDisk, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Th, Td, Empty, Kpi } from "@/components/common";
import { api, money, errMsg } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";

export default function AccountContacts() {
  const nav = useNavigate();
  const [data, reload] = useLoad(() => api.get("/accounts/missing-contact").then((r) => r.data), []);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState({});
  const rows = data?.rows || [];

  useEffect(() => { setDraft({}); setDone({}); }, [data]);

  const val = (r, k) => draft[r.id]?.[k] ?? r[k];
  const set = (r, k) => (e) => setDraft((s) => ({ ...s, [r.id]: { email: r.email, phone: r.phone, ...s[r.id], [k]: e.target.value } }));

  const save = async (r) => {
    setBusy(r.id);
    try {
      await api.patch(`/accounts/${r.id}/contact`, { email: val(r, "email").trim(), phone: val(r, "phone").trim() });
      setDone((s) => ({ ...s, [r.id]: true }));
      toast.success(`${r.name} güncellendi`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy("");
  };

  return (
    <div data-testid="account-contacts-page">
      <PageHeader eyebrow="Cari Kartları" title="Eksik İletişim Bilgileri">
        <Button variant="outline" className="rounded-[4px]" onClick={reload} data-testid="contacts-refresh-button">Listeyi Yenile</Button>
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Kpi label="Eksik Bilgili Cari" value={data ? String(data.count) : "…"} testid="contacts-count" />
        <Kpi label="E-postası Olmayan" value={data ? String(data.no_email) : "…"} tone="danger" testid="contacts-no-email" />
        <Kpi label="Telefonu Olmayan" value={data ? String(data.no_phone) : "…"} testid="contacts-no-phone" />
      </div>

      <div className="panel mt-6 overflow-x-auto">
        <table className="w-full min-w-[820px]">
          <thead>
            <tr><Th>Kod</Th><Th>Cari</Th><Th right>Açık Bakiye</Th><Th>E-posta</Th><Th>Telefon</Th><Th /></tr>
          </thead>
          <tbody>
            {data && rows.length === 0 && <Empty cols={6} />}
            {rows.map((r) => (
              <tr key={r.id} className="transition-colors hover:bg-muted/50" data-testid={`contact-row-${r.code}`}>
                <Td><button className="font-num underline-offset-2 hover:underline" onClick={() => nav(`/cariler/${r.id}`)} data-testid={`contact-open-${r.code}`}>{r.code}</button></Td>
                <Td>{r.name}{r.open_count > 0 && <span className="ml-2 text-[11px] text-muted-foreground">({r.open_count} açık fatura)</span>}</Td>
                <Td right>{money(r.open_amount)}</Td>
                <Td>
                  <Input value={val(r, "email")} onChange={set(r, "email")} placeholder="ornek@firma.com" type="email"
                    className={`h-9 ${!val(r, "email") ? "border-red-300" : ""}`} data-testid={`contact-email-${r.code}`} />
                </Td>
                <Td>
                  <Input value={val(r, "phone")} onChange={set(r, "phone")} placeholder="0532 000 00 00"
                    className={`h-9 ${!val(r, "phone") ? "border-amber-300" : ""}`} data-testid={`contact-phone-${r.code}`} />
                </Td>
                <Td>
                  <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => save(r)} className="rounded-[4px] whitespace-nowrap" data-testid={`contact-save-${r.code}`}>
                    {done[r.id] ? <CheckCircle size={14} weight="fill" className="text-teal-700" /> : <FloppyDisk size={14} />} Kaydet
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground"><AddressBook size={14} /> E-postası olmayan carilere fatura ve vade hatırlatması gönderilemez. Bilgileri burada doldurup kaydedebilirsiniz.</p>
    </div>
  );
}
